// ============================================
// ÉCRANS ÉTROITS — téléphone et tablette
// ============================================
// Ce qui ne peut pas s'écrire en classes Tailwind seules : choisir un rendu
// différent (cartes au lieu d'un tableau) selon la largeur, et signaler un
// contenu qui défile à l'horizontale.
//
// Convention : une requête « vraie » décrit un écran ÉTROIT. Quand
// matchMedia est absent (rendu hors navigateur) ou ne répond pas, on garde le
// rendu ordinateur, celui d'avant.

import React, { useCallback, useEffect, useRef, useState } from 'react';

/** Sous lg (1024 px) : téléphone et tablette. Les tableaux larges y deviennent des cartes. */
export const REQUETE_ECRAN_ETROIT = '(max-width: 1023.98px)';
/** Sous sm (640 px) : téléphone. */
export const REQUETE_TELEPHONE = '(max-width: 639.98px)';

function lireRequete(requete: string): boolean {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false;
  try {
    return window.matchMedia(requete)?.matches === true;
  } catch {
    return false;
  }
}

/** Suit une requête média ; relue au redimensionnement et à la rotation. */
export function useRequeteMedia(requete: string): boolean {
  const [vraie, setVraie] = useState(() => lireRequete(requete));
  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return;
    const liste = window.matchMedia(requete);
    if (!liste) return;
    const maj = () => setVraie(liste.matches === true);
    maj();
    if (typeof liste.addEventListener === 'function') {
      liste.addEventListener('change', maj);
      return () => liste.removeEventListener('change', maj);
    }
    // Safari < 14
    liste.addListener?.(maj);
    return () => liste.removeListener?.(maj);
  }, [requete]);
  return vraie;
}

/** Vrai sous 1024 px (téléphone, tablette). */
export const useEcranEtroit = () => useRequeteMedia(REQUETE_ECRAN_ETROIT);
/** Vrai sous 640 px (téléphone). */
export const useTelephone = () => useRequeteMedia(REQUETE_TELEPHONE);

/**
 * Conteneur qui défile à l'horizontale (petit tableau). Sur téléphone et
 * tablette, quand une partie reste cachée à droite, le bord s'estompe et une
 * ligne invite à faire glisser. Sur ordinateur, rien ne s'ajoute.
 */
export const DefilementHorizontal: React.FC<{
  children: React.ReactNode;
  className?: string;
  /** Nom de la zone pour les lecteurs d'écran. */
  libelle?: string;
}> = ({ children, className = '', libelle }) => {
  const zone = useRef<HTMLDivElement>(null);
  const [resteADroite, setResteADroite] = useState(false);

  const mesurer = useCallback(() => {
    const el = zone.current;
    if (!el) return;
    setResteADroite(el.scrollWidth - el.clientWidth - el.scrollLeft > 4);
  }, []);

  useEffect(() => {
    mesurer();
    if (typeof ResizeObserver === 'undefined' || !zone.current) return;
    const observateur = new ResizeObserver(mesurer);
    observateur.observe(zone.current);
    return () => observateur.disconnect();
  }, [mesurer]);

  return (
    <div>
      <div className="relative">
        <div ref={zone} onScroll={mesurer} role={libelle ? 'region' : undefined} aria-label={libelle}
          tabIndex={libelle ? 0 : undefined} className={`overflow-x-auto ${className}`}>
          {children}
        </div>
        {resteADroite && (
          <div aria-hidden="true" className="lg:hidden pointer-events-none absolute inset-y-0 right-0 w-8 bg-gradient-to-l from-white to-transparent" />
        )}
      </div>
      {resteADroite && (
        <p className="lg:hidden px-4 py-2 text-xs text-graphite border-t border-trait">Faites glisser vers la gauche pour voir toutes les colonnes.</p>
      )}
    </div>
  );
};
